import React from 'react';
import { Alert, Divider } from '@mui/material';
import { HeartLoading } from '../../../components/HeartLoading';
import { Button, ErrorIcon, SuccessIcon } from '../../../components';
import { ChangePasswordDialog, ProfileCard } from './components';
import { useProfileDetails } from './useProfileDetails';

type ProfileDetailsViewProps = Omit<
    ReturnType<typeof useProfileDetails>,
    'metaTags'
>;

export const ProfileDetailsView: React.FC<ProfileDetailsViewProps> = ({
    profile,
    gift,
    profileLoading,
    giftLoading,
    fullAddress,
    hasSavedQuery,
    changePasswordOpen,
    setChangePasswordOpen,
    passwordChangePending,
    passwordChangeState,
    dismissAlert,
    dismissPasswordAlert,
    changePasswordAction,
    onEditProfileClick,
}) => {
    if (profileLoading || giftLoading) {
        return <HeartLoading maxWidth={300} />;
    }

    return (
        <div
            data-testid="profile-details"
            className="rounded-[32px] shadow-high bg-white w-full sm:ml-8 sm:mr-0 mx-4 overflow-hidden max-w-[792px]"
        >
            {hasSavedQuery ? (
                <Alert
                    data-testid="profile-saved-alert"
                    severity="success"
                    onClose={dismissAlert}
                    iconMapping={{ success: <SuccessIcon /> }}
                >
                    Profile Updated successfully.
                </Alert>
            ) : null}
            {passwordChangeState !== undefined && passwordChangeState ? (
                <Alert
                    data-testid="password-change-success-alert"
                    severity="success"
                    onClose={dismissPasswordAlert}
                    iconMapping={{ success: <SuccessIcon /> }}
                >
                    Change password link send to your email account.
                </Alert>
            ) : null}
            {passwordChangeState !== undefined && !passwordChangeState ? (
                <Alert
                    data-testid="password-change-error-alert"
                    severity="error"
                    onClose={dismissPasswordAlert}
                    iconMapping={{ error: <ErrorIcon /> }}
                >
                    Unable to change the password, please try again!
                </Alert>
            ) : null}
            <div
                data-testid="profile-details-body"
                className="w-full flex flex-col items-center p-6"
            >
                <div
                    data-testid="profile-details-title"
                    className="text-heading-large text-primary mb-16"
                >
                    Profile Details
                </div>
                <ProfileCard
                    profile={profile}
                    gift={gift}
                />
                <div
                    data-testid="profile-info-list"
                    className="flex flex-col gap-4 max-w-[335px] w-full mt-8"
                >
                    <div
                        data-testid="profile-email"
                        className="text-label-medium-light text-primary"
                    >
                        {profile?.donor.email}
                    </div>
                    <div
                        data-testid="profile-phone"
                        className="text-label-medium-light text-primary"
                    >
                        {profile?.donor.phone?.number}
                    </div>
                    <div
                        data-testid="profile-address"
                        className="text-label-medium-light text-primary"
                    >
                        {fullAddress}
                    </div>
                    <Button
                        data-testid="edit-profile-button"
                        className="mt-4"
                        variant="link"
                        onClick={onEditProfileClick}
                    >
                        Edit Profile Details
                    </Button>
                    <Divider />
                    <Button
                        data-testid="change-password-button"
                        variant="link"
                        onClick={() => setChangePasswordOpen(true)}
                    >
                        Change Password
                    </Button>
                </div>
            </div>
            <ChangePasswordDialog
                open={changePasswordOpen}
                onClose={() => setChangePasswordOpen(false)}
                onConfirm={changePasswordAction}
                isPending={passwordChangePending}
            />
        </div>
    );
};
